import React, { useState, useEffect } from 'react'

import { Modal, PrimaryButton } from '@hm-group/fabric-components'
import { CartItem, BodyText, HeadingSmall, Label } from '@pb/components'
import { getLabelFromStorage, formatCurrency } from '@pb/utils'
import { useDispatch } from 'react-redux'

import { operationMoveToFavourite } from 'features/cart/cart-overview/duck/operations'
import { RESELL_BRAND } from 'features/checkout/constants'
import { clearOutOfStockItems as clearOutOfStockItemsCheckout } from 'features/checkout/duck/actions'

import { PARTIAL_OUT_OF_STOCK_MODAL_LOCATION } from './constants'
import styles from './PartialOutOfStockModal.scss'
import { PartialOutOfStockModal as PartialOutOfStockModalType, DetailRows } from './types'

const PartialOutOfStockModal: React.FC<PartialOutOfStockModalType> = ({
  oosItems,
  location,
  handleRedirectToCheckout,
  setShowPartialOOSModal,
  hasResellItems = false,
  hasOnlyResellItems = false,
  favoritesInfoKey = 'checkout.oos.added.to.favourites',
  isAllItemsOutOfStock = false,
}) => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(!!oosItems.length)

  useEffect(() => {
    if (oosItems.length) {
      setIsOpen(true)
    }
  }, [oosItems])

  if (isAllItemsOutOfStock) {
    return null
  }

  const isFromCart = location === PARTIAL_OUT_OF_STOCK_MODAL_LOCATION.cart
  const isFromCheckout = location === PARTIAL_OUT_OF_STOCK_MODAL_LOCATION.checkout

  const renderDetailRows = (data): DetailRows => [
    { property: <Label copyKey="sidecart.art.no" />, value: data.productCode },
    { property: <Label copyKey="sidecart.color" />, value: data.color },
    {
      property: <Label copyKey="sidecart.size" />,
      value: data.size,
    },
    {
      property: <Label copyKey="sidecart.total" />,
      value: formatCurrency(data.totalPrice && data.totalPrice.value),
    },
  ]

  const labels = {
    moveToFavourites: getLabelFromStorage('move.to.favorites'),
    clubPriceText: getLabelFromStorage('club.price.text'),
    delete: getLabelFromStorage('basket.page.remove'),
    quantity: getLabelFromStorage('order.quantity'),
  }

  const desciptionText = isFromCart
    ? 'shopping.bag.continue.to.checkout.oos.move.favourites.description'
    : 'checkout.oos.added.to.favourites'

  const handleClose = (): void => {
    if (isFromCheckout) {
      dispatch(clearOutOfStockItemsCheckout())
    } else if (isFromCart && setShowPartialOOSModal) {
      setShowPartialOOSModal(false)
    }
    setIsOpen(false)
  }

  const handleContinueToCheckOut = (): void => {
    if (isFromCart) {
      oosItems.map((oosItem) => dispatch(operationMoveToFavourite(oosItem.variantCode)))
      handleRedirectToCheckout?.()
    } else if (isFromCheckout) {
      dispatch(clearOutOfStockItemsCheckout())
      setIsOpen(false)
    }
  }

  return (
    <Modal
      closeLabel={getLabelFromStorage('general.close')}
      isOpen={isOpen}
      onClose={handleClose}
      size="medium"
      heading={getLabelFromStorage('checkout.oos.page.header')}
      content={
        <>
          <div className={styles.descriptionContainer}>
            <HeadingSmall Tag="p" className={styles.descriptionLabel}>
              <Label copyKey="checkout.oos.some.items" />
            </HeadingSmall>
            {isFromCart && (
              <BodyText className={styles.descriptionText}>
                <Label copyKey={desciptionText} />
              </BodyText>
            )}
            {isFromCheckout && hasResellItems && !hasOnlyResellItems && (
              <BodyText className={styles.descriptionText}>
                <Label copyKey={desciptionText} />
              </BodyText>
            )}
            {isFromCheckout && (
              <BodyText>
                <Label copyKey={favoritesInfoKey} />
              </BodyText>
            )}
          </div>
          <ul className={styles.list}>
            {oosItems.map((data) => (
              <li
                data-testid="partialOutOfStockModalListItem"
                key={data.productCode}
                className={styles.listItem}
              >
                <CartItem
                  data={data}
                  detailsRows={renderDetailRows(data)}
                  labels={labels}
                  hideActions
                  isExternalImage={data?.brand === RESELL_BRAND}
                />
              </li>
            ))}
          </ul>
          <div className={styles.buttonContainer}>
            <PrimaryButton onClick={() => handleContinueToCheckOut()}>
              {getLabelFromStorage('checkout.oos.button.continue.to.checkout')}
            </PrimaryButton>
          </div>
        </>
      }
    />
  )
}

export default PartialOutOfStockModal
